import { useEffect, useState } from 'react'
import { api, type Draft, type DraftRow } from '../api'
import { useRefreshOnActivate } from '../useActive'

/**
 * Emails — every draft across every campaign, and the review backlog.
 *
 * The same data the Campaign flow's Review step reads, from the other
 * direction. Reviewing inside a campaign is right when you are running one;
 * this is right when your job that morning is "clear whatever is waiting",
 * which spans campaigns. Two entry points to one table, not two mechanisms.
 */
export function EmailsTab({
  active = true,
  draftId,
  onOpenDraft,
  onCloseDraft,
}: {
  active?: boolean
  draftId?: number | null
  onOpenDraft?: (id: number) => void
  onCloseDraft?: () => void
}) {
  const [rows, setRows] = useState<DraftRow[] | null>(null)
  const [total, setTotal] = useState(0)
  const [statusFilter, setStatusFilter] = useState('pending')
  const [open, setOpen] = useState<Draft | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!active) return
    if (draftId == null) {
      setOpen(null)
      return
    }
    let live = true
    void api.draft(draftId).then((d) => {
      if (live) setOpen(d)
    })
    return () => {
      live = false
    }
  }, [active, draftId])

  const load = async () => {
    try {
      setError(null)
      const page = await api.drafts({ status_filter: statusFilter || undefined, limit: 200 })
      setRows(page.drafts)
      setTotal(page.total)
    } catch (err) {
      setError(String((err as Error).message))
    }
  }

  useEffect(() => {
    void load()
  }, [statusFilter])

  // This is the review backlog. Approving a draft in the campaign flow changes
  // what belongs here, so it refetches on return while keeping the chosen
  // status filter and any open draft.
  useRefreshOnActivate(active, load)

  if (error) return <div className="error">{error}</div>

  return (
    <>
      <div className="panel">
        <h2>
          Emails <span className="count">{total} matching</span>
        </h2>
        <div className="panel-body">
          <p className="hint">
            Drafts from every campaign. Defaults to <span className="mono">pending</span>, because
            the useful question here is usually "what is still waiting on me" — approval is
            required before anything sends, so a forgotten draft means nothing goes out rather than
            something going out unread.
          </p>
          <div className="row">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">all</option>
              <option value="pending">pending</option>
              <option value="revised">revised</option>
              <option value="approved">approved</option>
              <option value="rejected">rejected</option>
              <option value="sent">sent</option>
            </select>
            <button onClick={() => void load()}>Refresh</button>
          </div>
        </div>

        {!rows ? (
          <div className="loading">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="empty">
            Nothing {statusFilter ? `at status "${statusFilter}"` : 'yet'}.
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th className="num">Draft</th>
                <th>Campaign</th>
                <th className="num">Account</th>
                <th>Status</th>
                <th>Subject</th>
                <th>Model</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td className="num mono">{r.id}</td>
                  <td>{r.campaign}</td>
                  <td className="num mono">{r.account_id}</td>
                  <td>
                    <span
                      className={`badge ${
                        r.status === 'approved' || r.status === 'sent'
                          ? 'ok'
                          : r.status === 'rejected'
                            ? 'bad'
                            : 'catchall'
                      }`}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td>{r.refined_subject ?? r.original_subject}</td>
                  <td className="mono" style={{ fontSize: 11 }}>
                    {r.llm_model ?? '—'}
                  </td>
                  <td>
                    <button
                      onClick={() => {
                        if (onOpenDraft) onOpenDraft(r.id)
                        else void api.draft(r.id).then(setOpen)
                      }}
                    >
                      Compare
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {open && (
        <div className="panel">
          <h2>
            <button
              onClick={() => {
                if (onCloseDraft) onCloseDraft()
                else setOpen(null)
              }}
            >
              ← Back
            </button>
            Draft {open.id} <span className="count">account {open.account_id}</span>
          </h2>
          <div className="panel-body">
            <div className="grid2">
              <div>
                <label>Original — composed from approved fragments</label>
                <strong>{open.original_subject}</strong>
                <pre className="notes">{open.original_body}</pre>
              </div>
              <div>
                <label>
                  Refined {open.llm_model && <span className="mono">by {open.llm_model}</span>}
                </label>
                {open.refined_body ? (
                  <>
                    <strong>{open.refined_subject}</strong>
                    <pre className="notes">{open.refined_body}</pre>
                  </>
                ) : (
                  <div className="empty">
                    Not refined — either not run, or the validator rejected the output and the
                    original stands.
                  </div>
                )}
              </div>
            </div>
            <div className="row">
              <button
                className="primary"
                onClick={() =>
                  void api.review([open.id], 'approved').then(() => {
                    if (onCloseDraft) onCloseDraft()
                    else setOpen(null)
                    void load()
                  })
                }
              >
                Approve
              </button>
              <button
                className="danger"
                onClick={() =>
                  void api.review([open.id], 'rejected').then(() => {
                    if (onCloseDraft) onCloseDraft()
                    else setOpen(null)
                    void load()
                  })
                }
              >
                Reject
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}
